// A Leaflet tile layer through the tile front door (tiles.vortexnav.com), which counts every
// tile, on the portal's tile pass. Today's address stays as each tile's fallback, so a
// front-door failure never blanks a map. docs/planning/TILE_FRONT_DOOR_PLAN.md §3.1, §3.5.
//
// The pass rides in the tile URL (an <img> tile cannot send a header), so the visible tiles
// reload once when it renews: hourly signed out, every twelve hours signed in.
import L from 'leaflet';
import { TileLayer, type TileLayerProps } from 'react-leaflet';
import { useQuery } from '@tanstack/react-query';
import { fallbackTileUrl, frontDoorTemplate, providerForSource } from '@vortex/basemaps';
import { api, type TilePass } from '../../services/api';

/** The front-door template when the pass opens the source, else today's address alone. */
export function frontDoorLayerUrls(
  source: string,
  fallback: string,
  pass: Pick<TilePass, 'pass' | 'sources'> | null,
): { url: string; fallback: string | null } {
  const id = providerForSource(source)?.id;
  if (id && pass?.sources.includes(id)) return { url: frontDoorTemplate(id, pass.pass), fallback };
  return { url: fallback, fallback: null };
}

/** How long a map waits for its first tile pass before drawing from today's address. */
export const PASS_WAIT_MS = 3000;

/**
 * The portal's tile pass, renewed ten minutes before it runs out. `ready` once the first
 * answer is in, or after PASS_WAIT_MS, so a layer can wait and fetch its first view once;
 * `pass` null when it cannot be had.
 */
export function useTilePass(): { pass: TilePass | null; ready: boolean } {
  const query = useQuery({
    queryKey: ['tile-pass'],
    queryFn: () => Promise.race([
      api.getTilePass(),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('tile pass timed out')), PASS_WAIT_MS)),
    ]),
    retry: false,
    staleTime: 5 * 60_000,
    refetchInterval: (q) => {
      const ends = q.state.data ? Date.parse(q.state.data.expires_at) : NaN;
      return Number.isFinite(ends) ? Math.max(60_000, ends - Date.now() - 10 * 60_000) : 5 * 60_000;
    },
  });
  return { pass: query.data ?? null, ready: !query.isPending };
}

type Props = Omit<TileLayerProps, 'url'> & {
  /** The registry source (`osm`, `esri-imagery`, `sentinel2-cloudless`). */
  source: string;
  /** Today's address for these tiles: used when the pass does not open the source, and behind each front-door tile. */
  fallbackUrl: string;
};

export function FrontDoorTileLayer({ source, fallbackUrl, ...props }: Props) {
  const { pass, ready } = useTilePass();
  const { url, fallback } = frontDoorLayerUrls(source, fallbackUrl, pass);
  // Wait for the pass, so the first view is fetched once rather than direct and then again.
  if (!ready) return null;
  const subdomains = typeof props.subdomains === 'string' ? props.subdomains : (props.subdomains ?? ['a', 'b', 'c']).join('');
  return (
    <TileLayer
      {...props}
      url={url}
      eventHandlers={fallback ? {
        tileerror: (e) => {
          const ev = e as L.TileErrorEvent;
          const img = ev.tile as HTMLImageElement;
          if (img.dataset.fellBack) return;
          img.dataset.fellBack = '1';
          // Wrapped: Leaflet reports a tile past 180° by its unwrapped x.
          img.src = fallbackTileUrl(fallback, ev.coords, subdomains);
        },
      } : undefined}
    />
  );
}
